<template>
  <v-form ref="case-form" v-model="formValid" @input="handleFormInput">
    <v-row no-gutters>
      <v-col cols="12" class="case-header">
        <case-system-id class="case-system-id-editor" :systemId="systemId"
                        :progress="progress('setCurrentSystemId')"
                        @overrideSystemId="handleOverrideSystemId"></case-system-id>
        <v-checkbox
            class="case-form-item ml-3"
            label="All documents received"
            :indeterminate="editedCase.allDocumentsReceived === null"
            :rules="allDocumentsReceivedRules"
            v-model="allDocumentsReceived"
        ></v-checkbox>

        <v-spacer/>
        <div class="case-audit-info text-right">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on" class="ml-3 case-creation-label" v-if="editedCase.creationDatetime"><i
                  class="fas fa-calendar-plus"></i> {{ editedCase.creationDatetime | DateWithMinutes }}
              </div>
            </template>
            <span>Case creation datetime</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on" class="ml-3 case-creation-label" v-if="editedCase.updatedAt"><i
                  class="fas fa-edit"></i>{{ editedCase.updatedAt | DateWithMinutes }}
              </div>
            </template>

            <span>Case last update datetime</span>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" md="6">
        <v-menu
            v-model="materialsReceivedDatetimeMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
                :value="reformatDate(materialsReceivedDatetime)"
                :rules="materialsReceivedDatetimeRules"
                class="case-form-item"
                label="Materials received date"
                prepend-icon="event"
                readonly
                v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="editedCase.materialsReceivedDatetime"
                         @input="materialsReceivedDatetimeMenu = false"
                         first-day-of-week="1">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="handleClickMaterialReceivedDatetimeNoData">No data</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <patient-form ref="patient-form" :patient="editedCase.patient" :case-id="editedCase.id"
                      @patient-birthday-changed="handlePatientBirthdayChanged"
                      @validation-changed="handlePatientFormChange">
          <template slot="age-selector">
            <v-combobox
                ref="patientAge"
                v-model="patientAge"
                :disabled="patientAgeSelectorDisabled()"
                @update:search-input="handlePatientAgeInput"
                :items="noDataOptions"
                class="case-form-item"
                label="Age"
                suffix="yrs."
                :rules="ageRules"
            ></v-combobox>
          </template>
        </patient-form>
      </v-col>
    </v-row>
    <v-divider class="mt-3 mb-2"/>
    <v-row no-gutters>
      <v-col cols="12">
        <v-combobox
            ref="externalId"
            v-model="externalId"
            :items="noDataOptions"
            class="case-form-item"
            label="External Id"
            :rules="externalIdRules"
        ></v-combobox>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="4" sm="3" md="2">
        <v-checkbox
            class="case-form-item"
            label="Donor"
            :indeterminate="editedCase.donor === null"
            :rules="donorRules"
            v-model="donor"
        ></v-checkbox>
      </v-col>
      <v-col cols="8" sm="9" md="10">
        <v-combobox
            ref="donorNumber"
            v-model="donorNumber"
            :items="noDataOptions"
            class="case-form-item"
            label="Donor number"
            :rules="donorNumberRules"
        ></v-combobox>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" sm="3">
        <simple-birthday-selector
            ref="partner-birthday-selector"
            class="case-form-item"
            label="Partner birthday"
            :no-data-enabled="!isNewCase"
            @input="handleChangePartnerBirthday"
            @validation-changed="handlePartnerBirthdayValidationChanged"
            :birthday="editedCase.partnerBirthday"></simple-birthday-selector>
      </v-col>
      <v-col cols="12" sm="3">
        <v-combobox
            ref="partnerAge"
            v-model="partnerAge"
            :disabled="partnerAgeSelectorDisabled"
            @update:search-input="handlePartnerAgeInput"
            :items="noDataOptions"
            class="case-form-item"
            label="Partner age"
            suffix="yrs."
            :rules="ageRules"
        ></v-combobox>
      </v-col>
      <v-col cols="12" sm="6">
        <v-combobox
            ref="partnerName"
            v-model="partnerName"
            :items="noDataOptions"
            class="case-form-item"
            label="Partner name"
            :rules="partnerNameRules"
        ></v-combobox>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="4" sm="3" md="2">
        <v-checkbox
            class="case-form-item"
            label="Partner donor"
            :indeterminate="editedCase.partnerDonor === null"
            :rules="partnerDonorRules"
            v-model="partnerDonor"
        ></v-checkbox>
      </v-col>
      <v-col cols="8" sm="9" md="10">
        <v-combobox
            ref="partnerDonorNumber"
            v-model="partnerDonorNumber"
            :items="noDataOptions"
            class="case-form-item"
            label="Partner donor number"
            :rules="partnerDonorNumberRules"
        ></v-combobox>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="10" class="ml-3">
        <div style="display: flex;">
          <v-subheader class="ma-0 pa-0">Clinical data</v-subheader>
          <v-checkbox
              dense
              class="mt-2 ml-3"
              label="No data"
              @change="handleChangeNoClinicalData"
              :indeterminate="noClinicalData === null"
              :rules="noClinicalDataRules"
              v-model="noClinicalDataModel"
          ></v-checkbox>
        </div>
        <div v-if="noClinicalData === false">
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0" value="lateReproductiveAge" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['lateReproductiveAge'].text }}
              </div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="aneuploidyInPreviousPregnancy" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['aneuploidyInPreviousPregnancy'].text }}
              </div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="recurrentLackOfImplantation" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['recurrentLackOfImplantation'].text }}
              </div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="screening" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['screening'].text }}
              </div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="maleFactor" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['maleFactor'].text }}
              </div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="habitualMiscarriageOfPregnancy" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['habitualMiscarriageOfPregnancy'].text }}
              </div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="failedIVFAttempts" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['failedIVFAttempts'].text }}
              </div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="partnersTranslocation" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['partnersTranslocation'].text }}
              </div>
            </template>
          </v-checkbox>
          <div class="ml-5">
            <v-subheader class="text-body-16-nk pa-0" style="height: 32px;">Select region</v-subheader>
            <v-text-field v-model="clinicalDataPartnerTranslocationRegions" hide-details
                          outlined dense persistent-hint></v-text-field>
          </div>
          <v-checkbox v-model="clinicalData" class="ma-0 pa-0 mt-2" value="other" hide-details>
            <template v-slot:label>
              <div class="text-body-18-nk" style="margin-bottom: 0 !important;">
                {{ availableClinicalDataByValues['other'].text }}
              </div>
            </template>
          </v-checkbox>
          <div class="ml-5 mt-1">
            <v-textarea v-model="clinicalDataOtherDetails"
                        outlined dense persistent-hint></v-textarea>
          </div>
        </div>
      </v-col>
      <v-col cols="2" class="case-file-upload text-center">
        <div>
          <file-upload
              ref="upload"
              v-model="editedCase.files"
              input-id="uploadFileInCase"
              :post-action="uploadUrl"
              multiple
              :customAction="customAction"
              @input-file="inputFile">
            <i class="fas fa-file-upload"></i>
            <div>Attach file</div>
          </file-upload>
        </div>
      </v-col>
      <v-col>
        <file-list :items="editedCase.files" @delete="handleDeleteFile" @fileClick="fileViewerOpen($event)"/>
      </v-col>
    </v-row>
    <v-divider class="mb-2 mt-2"/>
    <v-row no-gutters>
      <v-col cols="12">
        <v-menu
            v-model="biopsyDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-combobox
                v-model="biopsyDates"
                :rules="biopsyDatesRules"
                multiple
                chips
                deletable-chips
                small-chips
                class="case-form-item"
                label="Biopsy dates"
                prepend-icon="event"
                v-on="on"
            >
              <template v-slot:selection="data">
                <v-chip v-if="data.item === ''"
                        class="chip--select-multi"
                >
                  No data
                </v-chip>
                <v-chip v-else
                        :input-value="data.selected"
                        class="chip--select-multi"
                        close
                        @click:close="handleRemoveBiopsyDate(data.item)"
                >
                  {{ reformatDate(data.item) }}
                </v-chip>
              </template>
            </v-combobox>
          </template>
          <v-date-picker v-model="biopsyDates" @input="biopsyDateMenu = false" multiple
                         first-day-of-week="1">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="handleClickBiopsyDateNoData">No data</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <v-textarea
            label="Comment"
            v-model="comment"
            class="case-form-item"
            hint="Please enter comment to case"
            auto-grow
            rows="3"
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" md="12">
        <v-autocomplete
            :items="availableClients"
            v-model="editedCase.clientId"
            item-value="id"
            item-text="title"
            no-data-text="No clients"
            class="case-form-item required-field"
            label="*Client"
            :rules="clientRules"
            required
            persistent-hint
            clearable
            @change="handleClientChange"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" md="12">
        <v-autocomplete
            ref="clientSpecialists"
            :items="availableClientSpecialists"
            v-model="clientSpecialistIds"
            item-value="id"
            item-text="name"
            no-data-text="No embryologists"
            class="case-form-item"
            label="Embryologists"
            :disabled="!editedCase.clientId"
            :rules="clientSpecialistIdsRules"
            clearable
            deletable-chips
            multiple
            small-chips
            persistent-hint
        >
          <v-list-item
              slot="prepend-item"
              ripple
              @click="handleSetSpecialistsNoData"
          >
            <v-list-item-title>No data</v-list-item-title>
          </v-list-item>
          <v-divider
              slot="prepend-item"
              class="mt-2"
          ></v-divider>
          <template v-slot:selection="data">
            <v-chip v-if="data.item.id === null"
                    class="chip--select-multi"
            >
              {{ data.item.name }}
            </v-chip>
            <v-chip v-else
                    :input-value="data.selected"
                    class="chip--select-multi"
                    close
                    @click:close="handleRemoveClientSpecialist(data.item.id)"
            >
              {{ data.item.name }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <template v-if="data.item.id !== null && !data.item.ex">
              <v-list-item-content v-text="data.item.name"></v-list-item-content>
            </template>
            <template v-else>
              <v-list-item style="display: none;"></v-list-item>
            </template>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
    <!--        <v-divider class="mt-3 mb-3"/>-->
    <!--        <v-layout>-->
    <!--            <v-flex xs12 md12>-->
    <!--                <case-services ref="case-services"-->
    <!--                               :clientId="editedCase.clientId"-->
    <!--                               :selectedServices="editedCase.services"-->
    <!--                               :read-only="!editedCase.clientId"-->
    <!--                               @addService="handleAddService"-->
    <!--                               @removeService="handleRemoveService"-->
    <!--                               @changeQuantity="handleChangeQuantity"></case-services>-->
    <!--            </v-flex>-->
    <!--        </v-layout>-->
    <v-divider class="mt-3 mb-3"/>
    <v-row no-gutters>
      <v-col>
        <samples-manager ref="samples-manager"
                         :samples="editedCase.samples"
                         :selectedClientId="editedCase.clientId"
                         @validation-changed="handleSamplesManagerValidationChanged"></samples-manager>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import FileList from '../../Files/List'
import PatientForm from './PatientForm';
import ContainerList from '../../Container/List'
import {mapActions, mapGetters, mapState} from 'vuex'
import NoEmpty from '../../Filters/NoEmpty'
import DateWithMinutes from '../../Filters/DateWithMinutes'
import CaseSystemId from './CaseSystemId'
import SamplesManager from '../../Samples/Manager'
import CaseServices from './CaseServices'
import SimpleBirthdaySelector from '../../Common/SimpleBirthdaySelector'
import {getBaseApiURL} from '../../../helpers/baseurl'
import _ from 'lodash'
import {noDataValue} from '../../../dictionaries/nodata'
import {getStringOrNull} from '../../../helpers/strings'
import {extractNodatableValue} from '../../../helpers/nodata'
import {reformatDate} from '../../../helpers/timeformats'
import {isNumeric} from "../../../helpers/numbers";

export default {
  name: "CaseForm",
  components: {
    FileList,
    ContainerList,
    PatientForm,
    SamplesManager,
    CaseServices,
    CaseSystemId,
    SimpleBirthdaySelector
  },
  props: {
    case: Object,
    patient: Object
  },
  filters: {
    NoEmpty,
    DateWithMinutes
  },
  data() {
    const editedCase = this.initEditedCase(this.case, this.patient)
    let newCase = !this.case
    let noClinicalData = null

    if (newCase && editedCase.clinicalData.length === 0) {
      noClinicalData = null
    }

    if (!newCase) {
      noClinicalData = editedCase.clinicalData.length === 0
    }

    return {
      subFormRefs: ['patient-form', 'samples-manager', 'partner-birthday-selector'],
      formValid: false,
      biopsyDateMenu: false,
      materialsReceivedDatetimeMenu: false,
      partnerBirthdayMenu: false,
      editedCase: editedCase,
      clientRules: [
        v => !!v || 'Select client'
      ],
      donorNumberRules: [
        v => !!v || 'Enter donor number'
      ],
      partnerNameRules: [
        v => !!v || 'Enter partner name'
      ],
      partnerDonorNumberRules: [
        v => !!v || 'Enter partner donor number'
      ],
      externalIdRules: [
        v => !!v || 'Enter external Id'
      ],
      clinicalDataRules: [
        v => !!v || 'Enter clinical data'
      ],
      noClinicalDataRules: [
        function (v) {
          if (v === null) {
            return 'Select clinical data'
          }

          return true
        }
      ],
      allDocumentsReceivedRules: [
        function (v) {
          if (v === null) {
            return 'Select document received'
          }

          return true
        }
      ],
      partnerDonorRules: [
        function (v) {
          if (v === null) {
            return 'Select partner donor'
          }

          return true
        }
      ],
      donorRules: [
        function (v) {
          if (v === null) {
            return 'Select donor'
          }

          return true
        }
      ],
      clientSpecialistIdsRules: [
        function (v) {

          if (!v || v.length === 0) {
            return 'Select embryologists'
          }

          return true
        }
      ],
      partnerBirthdayRules: [
        function (v) {
          if (v === null) {
            return 'Select partner birthday'
          }

          return true
        }
      ],
      materialsReceivedDatetimeRules: [
        function (v) {
          if (v === null) {
            return 'Select materials received date'
          }

          return true
        }
      ],
      biopsyDatesRules: [
        function (v) {
          if (!v || v.length === 0) {
            return 'Select biopsy dates'
          }

          return true
        }
      ],
      ageRules: [
        (v) => {
          if (v === null) {
            return 'Enter age'
          }

          return true
        },
        (v) => {
          if (v === null) {
            return true
          }

          if (v.type === noDataValue.type) {
            return true
          }

          const numeric = isNumeric(v)

          if (!numeric) {
            return 'Age must be numeric'
          }

          const age = parseInt(v)

          if (age < 0 && !this.innerBirthdayNotEmpty) {
            return 'Age must be greater than zero'
          }

          if (age > 150) {
            return 'Age must be lower than 150'
          }

          return true
        }
      ],
      noDataOptions: [noDataValue],
      noDataValue: noDataValue,
      noClinicalData: noClinicalData
    }
  },
  watch: {
    async case() {
      this.editedCase = this.initEditedCase(this.case)

      await this.$nextTick()
      this.$emit('init', this.getFormData())
    },
    'case.samples': function (newSamples) {
      this.editedCase.samples = newSamples
    }
  },
  computed: {
    ...mapGetters({
      availableClinicalDataByValues: 'availableClinicalDataByValues'
    }),
    ...mapGetters('pgtCases', {
      progress: 'progress'
    }),
    ...mapState('pgtCases', {
      currentSystemId: 'currentSystemId'
    }),
    ...mapState('clients', {
      availableClients: 'clients'
    }),
    ...mapGetters('clients', {
      clientsById: 'clientsById'
    }),
    uploadUrl() {
      return `${getBaseApiURL()}files/upload`
    },
    availableClientSpecialists() {
      const client = this.clientsById[this.editedCase.clientId]

      const specialists = [{'id': null, 'name': 'No Data'}]
      const clientSpecialists = _.get(client, 'specialists', [])
      let resultSpecialists = clientSpecialists

      if (clientSpecialists.length > 0) {
        resultSpecialists = _.concat(specialists, clientSpecialists)
      }

     //resultSpecialists = _.filter(resultSpecialists, (s) => {console.log(s); return !s.ex})

      return resultSpecialists
    },
    systemId() {
      return _.get(this.case, 'systemId', this.currentSystemId)
    },
    allDocumentsReceived: {
      get() {
        return this.editedCase.allDocumentsReceived
      },
      set(value) {
        this.editedCase.allDocumentsReceived = value
      }
    },
    noClinicalDataModel: {
      get() {
        return this.noClinicalData
      },
      set(value) {
        if (this.noClinicalData === null) {
          this.noClinicalData = false
        } else {
          this.noClinicalData = value
        }
      }
    },
    donor: {
      get() {
        return this.editedCase.donor
      },
      set(value) {
        // Меняем порядок вывода из нейтрального положения с true на false
        if (this.editedCase.donor === null) {
          this.editedCase.donor = false
        } else {
          this.editedCase.donor = value
        }

        if (!this.editedCase.donor && this.editedCase.donorNumber === null) {
          this.editedCase.donorNumber = noDataValue
        }

        /*if (this.editedCase.donor && this.editedCase.donorNumber === noDataValue) {
            this.editedCase.donorNumber = null
        }*/
      }
    },
    donorNumber: {
      get() {
        return this.getValueOrNoData(this.editedCase.donorNumber)
      },
      set(value) {
        this.editedCase.donorNumber = value

        const extractedValue = extractNodatableValue(value)
        // Ставим автоматическу галочку donor, если ввели значение
        if (extractedValue !== null) {
          this.editedCase.donor = true
        }
      }
    },
    partnerName: {
      get() {
        return this.getValueOrNoData(this.editedCase.partnerName)
      },
      set(value) {
        this.editedCase.partnerName = value
      }
    },
    partnerDonor: {
      get() {
        return this.editedCase.partnerDonor
      },
      set(value) {
        // Меняем порядок вывода из нейтрального положения с true на false
        if (this.editedCase.partnerDonor === null) {
          this.editedCase.partnerDonor = false
        } else {
          this.editedCase.partnerDonor = value
        }

        if (!this.editedCase.partnerDonor && this.editedCase.partnerDonorNumber === null) {
          this.editedCase.partnerDonorNumber = noDataValue
        }
      }
    },
    partnerDonorNumber: {
      get() {
        return this.getValueOrNoData(this.editedCase.partnerDonorNumber)
      },
      set(value) {
        this.editedCase.partnerDonorNumber = value

        const extractedValue = extractNodatableValue(value)
        // Ставим автоматическу галочку partner donor, если ввели значение
        if (extractedValue !== null) {
          this.editedCase.partnerDonor = true
        }
      }
    },
    externalId: {
      get() {
        return this.getValueOrNoData(this.editedCase.externalId)
      },
      set(value) {
        this.editedCase.externalId = value
      }
    },
    patientAge: {
      get() {
        return this.getValueOrNoData(this.editedCase.patientAge, this.patient)
      },
      set(value) {
        this.editedCase.patientAge = value
      }
    },
    partnerAge: {
      get() {
        return this.getValueOrNoData(this.editedCase.partnerAge)
      },
      set(value) {
        this.editedCase.partnerAge = value
      }
    },
    clinicalDataPartnerTranslocationRegions: {
      get() {
        return this.editedCase.clinicalDataPartnerTranslocationRegions
      },
      set(value) {
        let resultValue = getStringOrNull(value)

        if (resultValue !== null) {
          if (_.indexOf(this.clinicalData, 'partnersTranslocation') < 0) {
            this.clinicalData.push('partnersTranslocation')
          }
        }

        this.editedCase.clinicalDataPartnerTranslocationRegions = resultValue
      }
    },
    clinicalDataOtherDetails: {
      get() {
        return this.editedCase.clinicalDataOtherDetails
      },
      set(value) {
        let resultValue = getStringOrNull(value)

        if (resultValue !== null) {
          if (_.indexOf(this.clinicalData, 'other') < 0) {
            this.clinicalData.push('other')
          }
        }

        this.editedCase.clinicalDataOtherDetails = value
      }
    },
    comment: {
      get() {
        return this.editedCase.comment
      },
      set(value) {
        this.editedCase.comment = getStringOrNull(value)
      }
    },
    clientSpecialistIds: {
      get() {
        if (!this.isNewCase && this.editedCase.clientSpecialistIds.length === 0) {
          return [null]
        }

        return this.editedCase.clientSpecialistIds
      },
      set(value) {
        if (value.length === 1 && value[0] === null) {
          this.editedCase.clientSpecialistIds = value
          return
        }

        this.editedCase.clientSpecialistIds = _.without(value, null)
      }
    },
    materialsReceivedDatetime: {
      get() {
        if (this.editedCase.materialsReceivedDatetime === '' || (this.editedCase.materialsReceivedDatetime === null && !this.isNewCase)) {
          return 'No Data'
        }

        return this.editedCase.materialsReceivedDatetime
      },
      set(value) {
        this.editedCase.materialsReceivedDatetime = value
      }
    },
    biopsyDates: {
      get() {
        if (!this.isNewCase && this.editedCase.biopsyDates.length === 0) {
          return ['']
        }

        return this.editedCase.biopsyDates
      },
      set(value) {
        if (value.length === 1 && value[0] === '') {
          this.editedCase.biopsyDates = value
          return
        }

        this.editedCase.biopsyDates = _.without(value, '')
      }
    },
    isNewCase() {
      return !this.case
    },
    partnerAgeSelectorDisabled() {
      return this.editedCase.partnerBirthday !== null
    },
    clinicalData: {
      get() {
        return this.editedCase.clinicalData
      },
      set(value) {
        this.editedCase.clinicalData = value

        this.$emit('validation-changed', this.isValid())
      }
    },
  },
  async mounted() {
    await this.setCurrentSystemId()

    if (this.editedCase.clientId) {
      this.fetchClientServicesIfNeeded(this.editedCase.clientId)
    }

    this.$emit('init', this.getFormData())
  },
  methods: {
    ...mapActions({
      fileViewerOpen: 'fileViewerOpen'
    }),
    ...mapActions('pgtCases', {
      getAvailableSystemId: 'getAvailableSystemId',
      setCurrentSystemId: 'setCurrentSystemId'
    }),
    ...mapActions('services', {
      fetchClientServicesIfNeeded: 'fetchClientServicesIfNeeded'
    }),
    initEditedCase(caseData, patient = null) {
      let files = _.cloneDeep(_.get(this.case, 'files', []))

      if (files === null) {
        files = []
      }

      let resultPatient = _.cloneDeep(patient)

      if (_.has(caseData, 'patient')) {
        resultPatient = _.cloneDeep(_.get(caseData, 'patient', null))
      }

      return {
        id: _.cloneDeep(_.get(caseData, 'id', null)),
        allDocumentsReceived: _.cloneDeep(_.get(caseData, 'allDocumentsReceived', null)),
        overriddenSystemId: null,
        creationDatetime: _.cloneDeep(_.get(caseData, 'creationDatetime', null)),
        updatedAt: _.cloneDeep(_.get(caseData, 'updatedAt', null)),
        clientId: _.cloneDeep(_.get(caseData, 'clientId', null)),
        clientSpecialistIds: _.cloneDeep(_.get(caseData, 'clientSpecialistIds', null)),
        title: _.cloneDeep(_.get(caseData, 'title', null)),
        clinicalData: _.cloneDeep(_.get(caseData, 'clinicalData', [])),
        clinicalDataPartnerTranslocationRegions: _.cloneDeep(_.get(caseData, 'clinicalDataPartnerTranslocationRegions', null)),
        clinicalDataOtherDetails: _.cloneDeep(_.get(caseData, 'clinicalDataOtherDetails', null)),
        donor: _.cloneDeep(_.get(caseData, 'donor', null)),
        donorNumber: _.cloneDeep(_.get(caseData, 'donorNumber', null)),
        partnerName: _.cloneDeep(_.get(caseData, 'partnerName', null)),
        partnerBirthday: _.cloneDeep(_.get(caseData, 'partnerBirthday', null)),
        partnerDonor: _.cloneDeep(_.get(caseData, 'partnerDonor', null)),
        partnerDonorNumber: _.cloneDeep(_.get(caseData, 'partnerDonorNumber', null)),
        externalId: _.cloneDeep(_.get(caseData, 'externalId', null)),
        biopsyDates: _.cloneDeep(_.get(caseData, 'biopsyDates', [])),
        patientAge: _.cloneDeep(_.get(caseData, 'patientAge', null)),
        partnerAge: _.cloneDeep(_.get(caseData, 'partnerAge', null)),
        materialsReceivedDatetime: _.cloneDeep(_.get(caseData, 'materialsReceivedDatetime', null)),
        status: _.cloneDeep(_.get(caseData, 'status', null)),
        patient: resultPatient,
        files: files,
        samples: _.cloneDeep(_.get(caseData, 'samples', [])),
        services: _.cloneDeep(_.get(caseData, 'services', [])),
        comment: _.cloneDeep(_.get(caseData, 'comment', null))
      }
    },
    handleClientChange() {
      this.clientSpecialistIds = []
      const client = this.clientsById[this.editedCase.clientId]

      this.fetchClientServicesIfNeeded(this.editedCase.clientId)
      this.editedCase.services = []
    },
    reset() {
      this.$refs['patient-form'].reset()
      this.editedCase = {
        id: null,
        overriddenSystemId: null,
        creationDatetime: null,
        clientId: null,
        clientSpecialistIds: null,
        title: null,
        clinicalData: null,
        donor: false,
        donorNumber: null,
        partnerName: null,
        partnerBirthday: null,
        partnerDonor: false,
        partnerDonorNumber: null,
        externalId: null,
        biopsyDates: [],
        patientAge: null,
        partnerAge: null,
        materialsReceivedDatetime: null,
        status: null,
        patient: null,
        files: [],
        services: [],
        comment: null,
      }
    },
    isValid() {
      return this.formValid && this.isSubFormsValid() && this.isNoClinicalDataValid()
    },
    validate() {
      return this.$refs['case-form'].validate() && this.validateSubForms() && this.isNoClinicalDataValid()
    },
    isNoClinicalDataValid() {
      if (this.noClinicalData === false && this.editedCase.clinicalData.length === 0) {
        return false
      }

      return true
    },
    getValueOrNoData(value, patient = null) {
      if ((!this.isNewCase || patient !== null) && value === null) {
        return noDataValue
      }

      return value
    },
    getFormData() {
      const patientData = this.$refs['patient-form'].getFormData()
      const samplesData = this.$refs['samples-manager'].getSamples()

      let resultClientSpecialists = _.without(this.editedCase.clientSpecialistIds, null)

      const patientAgeStr = extractNodatableValue(this.editedCase.patientAge)
      let patientAge = null
      if (patientAgeStr !== null) {
        patientAge = parseInt(patientAgeStr)
      }

      const partnerAgeStr = extractNodatableValue(this.editedCase.partnerAge)
      let partnerAge = null
      if (partnerAgeStr !== null) {
        partnerAge = parseInt(partnerAgeStr)
      }

      return {
        ...this.editedCase,
        files: this.editedCase.files,
        clientSpecialistIds: resultClientSpecialists,
        donorNumber: getStringOrNull(extractNodatableValue(this.editedCase.donorNumber)),
        partnerName: getStringOrNull(extractNodatableValue(this.editedCase.partnerName)),
        partnerDonorNumber: getStringOrNull(extractNodatableValue(this.editedCase.partnerDonorNumber)),
        externalId: getStringOrNull(extractNodatableValue(this.editedCase.externalId)),
        clinicalData: this.editedCase.clinicalData,
        clinicalDataPartnerTranslocationRegions: this.editedCase.clinicalDataPartnerTranslocationRegions,
        clinicalDataOtherDetails: this.editedCase.clinicalDataOtherDetails,
        partnerBirthday: this.editedCase.partnerBirthday === '' ? null : this.editedCase.partnerBirthday,
        materialsReceivedDatetime: this.editedCase.materialsReceivedDatetime === '' ? null : this.editedCase.materialsReceivedDatetime,
        biopsyDates: _.without(this.editedCase.biopsyDates, ''),
        patientAge: patientAge,
        partnerAge: partnerAge,
        systemId: this.systemId,
        patient: patientData,
        samples: samplesData
      }
    },
    handleFormInput() {
      this.$emit('validation-changed', this.isValid())
    },
    handlePatientFormChange() {
      this.$emit('validation-changed', this.isValid())
    },
    handleSamplesManagerValidationChanged() {
      this.$emit('validation-changed', this.isValid())
    },
    handlePartnerBirthdayValidationChanged() {
      this.$emit('validation-changed', this.isValid())
    },
    isSubFormsValid() {
      let validationResult = true

      for (let subFormRef of this.subFormRefs) {
        const subForm = _.get(this.$refs, subFormRef)

        if (!subForm) {
          continue
        }

        if (!subForm.isValid()) {
          validationResult = false
          break
        }
      }

      return validationResult
    },
    validateSubForms() {
      let validationResult = true

      for (let subFormRef of this.subFormRefs) {
        const subForm = _.get(this.$refs, subFormRef)

        if (!subForm) {
          continue
        }

        if (!subForm.validate()) {
          validationResult = false
        }
      }

      return validationResult
    },
    inputFile(newFile, oldFile) {
      // Automatic upload
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (!this.$refs.upload.active) {
          this.$refs.upload.active = true
        }
      }
    },
    async customAction(file, component) {
      let result = await component.uploadHtml5(file)
      const response = result.response

      if (response.error !== null) {
        let resultErrors = []

        if (_.isString(response.error)) {
          resultErrors.push(response.error)
        }

        if (_.isObject(response.error)) {
          for (let key in response.error) {
            if (!response.error.hasOwnProperty(key)) {
              continue
            }

            resultErrors.push(response.error[key])
          }
        }
        result.error = _.join(resultErrors, '; ')
        result.success = false

        this.$snotify.error(`Unable to upload file '${file.name}'. Please try again later`)
        return result
      }

      let fileId = _.get(response.data, 'fileId', null)

      if (fileId === null) {
        result.error = this.$t('files.errors.unableToUploadFile')
        result.success = false

        this.$snotify.error(`Unable to upload file '${file.name}'. Please try again later`)

        return result
      }

      result.fileId = fileId
      result.mimeType = _.get(response.data, 'mimeType', null)

      return result
    },
    handleDeleteFile(fileId) {
      let fileIndex = _.findIndex(this.editedCase.files, function (file) {
        return file.fileId === fileId
      })

      if (fileIndex < 0) {
        return
      }

      this.editedCase.files.splice(fileIndex, 1)
    },
    handleAddService(serviceId) {
      this.editedCase.services.push({
        serviceId: serviceId,
        quantity: 1
      })
    },
    handleRemoveService(serviceId) {
      const serviceIndex = _.findIndex(this.editedCase.services, (s) => {
        return s.serviceId === serviceId
      })

      if (serviceIndex < 0) {
        return
      }
      this.editedCase.services.splice(serviceIndex, 1)
    },
    handleChangeQuantity({serviceId, quantity}) {
      const serviceIndex = _.findIndex(this.editedCase.services, (s) => {
        return s.serviceId === serviceId
      })

      if (serviceIndex < 0) {
        return
      }

      this.editedCase.services[serviceIndex].quantity = quantity
    },
    handleOverrideSystemId(overriddenSystemId) {
      this.editedCase.overriddenSystemId = overriddenSystemId
    },
    handleSetSpecialistsNoData() {
      this.clientSpecialistIds = [null]
      this.$refs['clientSpecialists'].blur()
    },
    handleClickMaterialReceivedDatetimeNoData() {
      this.materialsReceivedDatetime = ''
      this.materialsReceivedDatetimeMenu = false
    },
    handleClickBiopsyDateNoData() {
      this.biopsyDates = ['']
      this.biopsyDateMenu = false
    },
    handleRemoveClientSpecialist(specialistId) {
      this.clientSpecialistIds = _.without(this.clientSpecialistIds, specialistId)
    },
    handleRemoveBiopsyDate(date) {
      this.biopsyDates = _.without(this.biopsyDates, date)
    },
    handlePatientAgeInput(value) {
      if (this.editedCase.patientAge === value || value === noDataValue.text) {
        return
      }

      if (value === null) {
        this.editedCase.patientAge = ''
        return
      }

      this.editedCase.patientAge = value
    },
    handlePartnerAgeInput(value) {
      if (this.editedCase.partnerAge === value || value === noDataValue.text) {
        return
      }

      if (value === null) {
        this.editedCase.partnerAge = ''
        return
      }

      this.editedCase.partnerAge = value
    },
    patientAgeSelectorDisabled() {
      if (!this.$refs['patient-form']) {
        return false
      }

      const patientBirthday = _.get(this.$refs['patient-form'].getFormData(), 'birthday', null)

      return patientBirthday !== null
    },
    handleChangePartnerBirthday(value) {
      if (value) {
        this.editedCase.partnerAge = noDataValue
      }
      this.editedCase.partnerBirthday = value
    },
    handlePatientBirthdayChanged(value) {
      if (value) {
        this.editedCase.patientAge = noDataValue
      }
    },
    handleChangeNoClinicalData() {
      if (this.noClinicalData === true) {
        this.editedCase.clinicalData = []
      }
    },
    reformatDate: reformatDate
  }
}
</script>

<style scoped>
.case-form-item {
  margin-left: 10px !important;
  margin-right: 10px !important;
  color: #000000;
}

.case-file-upload i {
  font-size: 44px;
  color: #1976d2
}

.case-file-upload > div {
  margin-top: 10px;
  cursor: pointer;
}

.case-audit-info {
  font-size: 12px;
  color: #636363;
}

.case-header {
  display: flex;
}
</style>

<style>
.case-file-upload label {
  cursor: pointer;
}

.case-header .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>